import { useCallback, useRef, useEffect } from 'react';

type Props = {
  delay: number,
  maxRetry: number,
};

export const useAsyncRetry = (fn, { delay = 500, maxRetry = 10, retryCondition }: Props = {}) => {
  const attempt = useRef(0);
  const loading = useRef(false);
  const unmounted = useRef(false);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  const delayMS = ms =>
    new Promise(res => {
      setTimeout(() => {
        res();
      }, ms);
    });

  const onRetry = useCallback(
    async (...args) => {
      attempt.current += 1;

      if (unmounted.current) {
        return Promise.reject(new Error('stop retries, useAsyncRetry is unmounted'));
      }

      if (loading.current) {
        return Promise.reject(new Error('useAsyncRetry is loading'));
      }

      try {
        loading.current = true;
        const payload = await fn(...args);
        if (retryCondition && retryCondition(payload)) {
          throw new Error('useAsyncRetry failed condition');
        }
        return payload;
      } catch (e) {
        loading.current = false;
        if (attempt.current >= maxRetry) {
          return Promise.reject(e);
        }
        await delayMS(delay);
        return onRetry(...args);
      } finally {
        loading.current = false;
      }
    },
    [fn, delay, maxRetry, attempt, loading, unmounted]
  );
  return onRetry;
};
